import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';

export const OralExamStage = {
  preFlightLobby: 'pre_flight_lobby',
  groupBriefing: 'group_briefing',
  examHandshake: 'exam_handshake',
  examConduct: 'exam_conduct',
  postExamWrapUp: 'post_exam_wrap_up',
} as const;

export type OralExamStageValue = typeof OralExamStage[keyof typeof OralExamStage];

export const OralExamStageGateCode = {
  ok: 'ok',
  inconsistentInExamPreStart: 'inconsistent_in_exam_pre_start',
} as const;

export type OralExamStageGateCodeValue = typeof OralExamStageGateCode[keyof typeof OralExamStageGateCode];

export type StageGateRunSnapshot = {
  state: string;
  sessionPhase: OralExamSessionPhase;
  conductPhase?: OralExamConductPhase | null;
  examinerReadyAt?: string | null;
  studentReadyAt?: string | null;
  committed?: boolean;
};

export type StageGateCohortSnapshot = {
  sessionPhase: OralExamSessionPhase;
  inExamEnteredAt?: string | null;
  admittedBookingIds: string[];
};

export type StageGateResult = {
  stage: OralExamStageValue;
  ok: boolean;
  code: OralExamStageGateCodeValue;
  messageKey: string;
};

function isBookingAdmitted(cohort: StageGateCohortSnapshot, bookingId: string): boolean {
  return cohort.admittedBookingIds.includes(bookingId);
}

export function resolveEffectiveSessionPhase(
  run: StageGateRunSnapshot,
  cohort: StageGateCohortSnapshot,
  bookingId: string,
): OralExamSessionPhase {
  if (cohort.sessionPhase === OralExamSessionPhase.post_exam) {
    return OralExamSessionPhase.post_exam;
  }
  if (
    cohort.sessionPhase === OralExamSessionPhase.group_briefing
    || cohort.sessionPhase === OralExamSessionPhase.in_exam
  ) {
    return isBookingAdmitted(cohort, bookingId)
      ? cohort.sessionPhase
      : OralExamSessionPhase.pre_flight;
  }
  return cohort.sessionPhase;
}

function isInconsistentInExamPreStart(
  run: StageGateRunSnapshot,
  cohort: StageGateCohortSnapshot,
  bookingId: string,
): boolean {
  if (cohort.sessionPhase === OralExamSessionPhase.post_exam) {
    return false;
  }
  return (
    Boolean(cohort.inExamEnteredAt)
    && isBookingAdmitted(cohort, bookingId)
    && run.state === 'pre_start'
  );
}

function deriveConductPhase(
  run: StageGateRunSnapshot,
  sessionPhase: OralExamSessionPhase,
): OralExamConductPhase | null {
  if (sessionPhase !== OralExamSessionPhase.in_exam || run.state !== 'in_progress') {
    return null;
  }
  if (run.committed) {
    return OralExamConductPhase.in_exam;
  }
  if (!run.examinerReadyAt) {
    return OralExamConductPhase.waiting_for_examiner;
  }
  if (!run.studentReadyAt) {
    return OralExamConductPhase.waiting_for_student;
  }
  return OralExamConductPhase.starting_exam;
}

export function resolveIntendedStage(
  run: StageGateRunSnapshot,
  cohort: StageGateCohortSnapshot,
  bookingId: string,
  effectiveSessionPhase = resolveEffectiveSessionPhase(run, cohort, bookingId),
): OralExamStageValue {
  if (effectiveSessionPhase === OralExamSessionPhase.post_exam) {
    return OralExamStage.postExamWrapUp;
  }
  if (effectiveSessionPhase === OralExamSessionPhase.group_briefing) {
    return OralExamStage.groupBriefing;
  }
  if (effectiveSessionPhase === OralExamSessionPhase.pre_flight) {
    return OralExamStage.preFlightLobby;
  }
  if (effectiveSessionPhase === OralExamSessionPhase.in_exam) {
    if (run.state !== 'in_progress') {
      return OralExamStage.examHandshake;
    }
    const conduct = deriveConductPhase(run, effectiveSessionPhase);
    if (
      conduct === OralExamConductPhase.waiting_for_examiner
      || conduct === OralExamConductPhase.waiting_for_student
      || conduct === OralExamConductPhase.starting_exam
      || conduct === null
    ) {
      return OralExamStage.examHandshake;
    }
    return OralExamStage.examConduct;
  }
  return OralExamStage.preFlightLobby;
}

export function evaluateStageGate(
  run: StageGateRunSnapshot,
  cohort: StageGateCohortSnapshot,
  bookingId: string,
): StageGateResult {
  const effective = resolveEffectiveSessionPhase(run, cohort, bookingId);
  const stage = resolveIntendedStage(run, cohort, bookingId, effective);

  if (isInconsistentInExamPreStart(run, cohort, bookingId)) {
    return {
      stage,
      ok: false,
      code: OralExamStageGateCode.inconsistentInExamPreStart,
      messageKey: 'oralExam.stageGate.inconsistentInExamPreStart',
    };
  }

  return {
    stage,
    ok: true,
    code: OralExamStageGateCode.ok,
    messageKey: 'oralExam.stageGate.ok',
  };
}

export function resolveCohortIntendedStage(
  cohort: StageGateCohortSnapshot,
  runsByBooking: Record<string, StageGateRunSnapshot>,
): OralExamStageValue {
  if (cohort.sessionPhase === OralExamSessionPhase.post_exam) {
    return OralExamStage.postExamWrapUp;
  }
  if (cohort.sessionPhase === OralExamSessionPhase.group_briefing) {
    return OralExamStage.groupBriefing;
  }
  if (cohort.sessionPhase === OralExamSessionPhase.pre_flight) {
    return OralExamStage.preFlightLobby;
  }
  if (cohort.inExamEnteredAt) {
    for (const bookingId of cohort.admittedBookingIds) {
      const run = runsByBooking[bookingId];
      if (run && run.state === 'in_progress') {
        const conduct = deriveConductPhase(run, OralExamSessionPhase.in_exam);
        if (
          conduct === OralExamConductPhase.waiting_for_examiner
          || conduct === OralExamConductPhase.waiting_for_student
          || conduct === OralExamConductPhase.starting_exam
          || conduct === null
        ) {
          return OralExamStage.examHandshake;
        }
      }
    }
    return OralExamStage.examConduct;
  }
  return OralExamStage.preFlightLobby;
}

export function evaluateCohortStageGate(
  cohort: StageGateCohortSnapshot,
  runsByBooking: Record<string, StageGateRunSnapshot>,
): StageGateResult {
  const stage = resolveCohortIntendedStage(cohort, runsByBooking);

  if (cohort.inExamEnteredAt && cohort.sessionPhase !== OralExamSessionPhase.post_exam) {
    for (const bookingId of cohort.admittedBookingIds) {
      const run = runsByBooking[bookingId];
      if (run && isInconsistentInExamPreStart(run, cohort, bookingId)) {
        return {
          stage,
          ok: false,
          code: OralExamStageGateCode.inconsistentInExamPreStart,
          messageKey: 'oralExam.stageGate.inconsistentInExamPreStart',
        };
      }
    }
  }

  return {
    stage,
    ok: true,
    code: OralExamStageGateCode.ok,
    messageKey: 'oralExam.stageGate.ok',
  };
}
